<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useRoute, useRouter } from 'vue-router';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';

import Policy from 'dashboard/components/policy.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SearchHeader from './SearchHeader.vue';
import SearchTabs from './SearchTabs.vue';
import SearchResultConversationsList from './SearchResultConversationsList.vue';
import SearchResultMessagesList from './SearchResultMessagesList.vue';
import SearchResultContactsList from './SearchResultContactsList.vue';
import SearchResultArticlesList from './SearchResultArticlesList.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();

const PER_PAGE = 15;
const TYPE_TABS = ['all', 'contacts', 'conversations', 'messages', 'articles'];
const FIELD_TABS = ['ic', 'email', 'whatsapp', 'phone'];
const selectedTab = ref(
  TYPE_TABS.includes(route.query.type) ? route.query.type : 'all'
);
const selectedField = ref(
  FIELD_TABS.includes(String(route.query.field || ''))
    ? String(route.query.field)
    : 'all'
);
const query = ref(String(route.query.q || '').trim());
const pages = ref({
  contacts: 1,
  conversations: 1,
  messages: 1,
  articles: 1,
});

const contactRecords = useMapGetter('conversationSearch/getContactRecords');
const conversationRecords = useMapGetter(
  'conversationSearch/getConversationRecords'
);
const messageRecords = useMapGetter('conversationSearch/getMessageRecords');
const articleRecords = useMapGetter('conversationSearch/getArticleRecords');
const uiFlags = useMapGetter('conversationSearch/getUIFlags');

const safeList = value => (Array.isArray(value) ? value : []);

const mappedContacts = computed(() =>
  safeList(contactRecords.value).map(item => ({ ...item, type: 'contact' }))
);
const mappedConversations = computed(() =>
  safeList(conversationRecords.value).map(item => ({
    ...item,
    type: 'conversation',
  }))
);
const mappedMessages = computed(() =>
  safeList(messageRecords.value).map(item => ({ ...item, type: 'message' }))
);
const mappedArticles = computed(() =>
  safeList(articleRecords.value).map(item => ({ ...item, type: 'article' }))
);

const isSelectedTabAll = computed(() => selectedTab.value === 'all');

const contacts = computed(() =>
  isSelectedTabAll.value
    ? mappedContacts.value.slice(0, 5)
    : mappedContacts.value
);
const conversations = computed(() =>
  isSelectedTabAll.value
    ? mappedConversations.value.slice(0, 5)
    : mappedConversations.value
);
const messages = computed(() =>
  isSelectedTabAll.value
    ? mappedMessages.value.slice(0, 5)
    : mappedMessages.value
);
const articles = computed(() =>
  isSelectedTabAll.value
    ? mappedArticles.value.slice(0, 5)
    : mappedArticles.value
);

const showContactsSection = computed(
  () =>
    selectedTab.value === 'contacts' ||
    (isSelectedTabAll.value &&
      (mappedContacts.value.length > 0 || uiFlags.value?.contact?.isFetching))
);
const showConversationsSection = computed(
  () =>
    selectedTab.value === 'conversations' ||
    (isSelectedTabAll.value &&
      (mappedConversations.value.length > 0 ||
        uiFlags.value?.conversation?.isFetching))
);
const showMessagesSection = computed(
  () =>
    selectedTab.value === 'messages' ||
    (isSelectedTabAll.value &&
      (mappedMessages.value.length > 0 || uiFlags.value?.message?.isFetching))
);
const showArticlesSection = computed(
  () =>
    selectedTab.value === 'articles' ||
    (isSelectedTabAll.value &&
      (mappedArticles.value.length > 0 || uiFlags.value?.article?.isFetching))
);

const { shouldShow, isFeatureFlagEnabled } = usePolicy();

const totalSearchResultsCount = computed(
  () =>
    mappedContacts.value.length +
    mappedConversations.value.length +
    mappedMessages.value.length +
    mappedArticles.value.length
);

const TABS_CONFIG = {
  all: {
    permissions: [
      CONTACT_PERMISSIONS,
      ...ROLES,
      ...CONVERSATION_PERMISSIONS,
      PORTAL_PERMISSIONS,
    ],
    count: () => totalSearchResultsCount.value,
  },
  contacts: {
    permissions: [...ROLES, CONTACT_PERMISSIONS],
    count: () => mappedContacts.value.length,
  },
  conversations: {
    permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    count: () => mappedConversations.value.length,
  },
  messages: {
    permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    count: () => mappedMessages.value.length,
  },
  articles: {
    permissions: [...ROLES, PORTAL_PERMISSIONS],
    featureFlag: FEATURE_FLAGS.HELP_CENTER,
    count: () => mappedArticles.value.length,
  },
};

const tabs = computed(() => {
  const typeTabs = Object.entries(TABS_CONFIG)
    .map(([key, config]) => ({
      key,
      name: t(`SEARCH.TABS.${key.toUpperCase()}`),
      count: config.count(),
      showBadge: true,
      permissions: config.permissions,
      featureFlag: config.featureFlag,
    }))
    .filter(config => {
      return (
        shouldShow(config.featureFlag, config.permissions, null) &&
        isFeatureFlagEnabled(config.featureFlag)
      );
    });

  const fieldTabs = FIELD_TABS.map(key => ({
    key,
    name: t(`SEARCH.FILTERS.${key.toUpperCase()}`),
    count: null,
    showBadge: false,
    permissions: [...ROLES, CONTACT_PERMISSIONS, ...CONVERSATION_PERMISSIONS],
    featureFlag: null,
  })).filter(config => shouldShow(null, config.permissions, null));

  return [...typeTabs, ...fieldTabs];
});

const activeNavKey = computed(() =>
  selectedField.value !== 'all' ? selectedField.value : selectedTab.value
);

const activeTabIndex = computed(() => {
  const index = tabs.value.findIndex(tab => tab.key === activeNavKey.value);
  return index >= 0 ? index : 0;
});

const isFetchingAny = computed(() => {
  const flags = uiFlags.value || {};
  return Boolean(
    flags.isFetching ||
      flags.contact?.isFetching ||
      flags.message?.isFetching ||
      flags.conversation?.isFetching ||
      flags.article?.isFetching
  );
});

const showEmptySearchResults = computed(
  () =>
    totalSearchResultsCount.value === 0 &&
    uiFlags.value?.isSearchCompleted &&
    !isFetchingAny.value &&
    query.value
);

const showResultsSection = computed(
  () =>
    isFetchingAny.value ||
    totalSearchResultsCount.value > 0 ||
    (query.value && !isSelectedTabAll.value)
);

const showLoadMore = computed(() => {
  if (
    !query.value ||
    isFetchingAny.value ||
    selectedTab.value === 'all' ||
    selectedField.value !== 'all'
  ) {
    return false;
  }

  const records = {
    contacts: mappedContacts.value,
    conversations: mappedConversations.value,
    messages: mappedMessages.value,
    articles: mappedArticles.value,
  }[selectedTab.value];

  return (
    records?.length > 0 &&
    records.length === pages.value[selectedTab.value] * PER_PAGE
  );
});

const showViewMore = computed(() => ({
  contacts: mappedContacts.value?.length > 5 && isSelectedTabAll.value,
  conversations:
    mappedConversations.value?.length > 5 && isSelectedTabAll.value,
  messages: mappedMessages.value?.length > 5 && isSelectedTabAll.value,
  articles: mappedArticles.value?.length > 5 && isSelectedTabAll.value,
}));

const searchTypes = computed(() => {
  if (selectedField.value !== 'all') {
    return ['contacts', 'conversations'];
  }
  return TYPE_TABS.filter(key => key !== 'all');
});

const syncRouteQuery = () => {
  if (!query.value) {
    router.replace({ query: {} });
    return;
  }
  router.replace({
    query: {
      q: query.value,
      type: selectedTab.value,
      ...(selectedField.value !== 'all' ? { field: selectedField.value } : {}),
    },
  });
};

const clearSearchResult = () => {
  pages.value = { contacts: 1, conversations: 1, messages: 1, articles: 1 };
  store.dispatch('conversationSearch/clearSearchResults');
};

const runSearch = () => {
  if (!query.value) return;
  clearSearchResult();
  useTrack(CONVERSATION_EVENTS.SEARCH_CONVERSATION);
  store.dispatch('conversationSearch/fullSearch', {
    q: query.value,
    page: 1,
    types: searchTypes.value,
    field: selectedField.value,
  });
};

const onSearch = q => {
  const normalizedQuery = String(q || '')
    .trim()
    .replace(/\s+/g, ' ');
  query.value = normalizedQuery;
  syncRouteQuery();
  if (!normalizedQuery) {
    clearSearchResult();
    return;
  }
  runSearch();
};

const onTabChange = key => {
  if (FIELD_TABS.includes(key)) {
    const changed = selectedField.value !== key;
    selectedField.value = key;
    selectedTab.value = 'all';
    syncRouteQuery();
    if (changed && query.value) runSearch();
    return;
  }

  const shouldResearch = selectedField.value !== 'all';
  selectedField.value = 'all';
  selectedTab.value = key;
  syncRouteQuery();
  if (shouldResearch && query.value) runSearch();
};

const onBack = () => {
  if (window.history.length > 2) {
    router.go(-1);
  } else {
    router.push({ name: 'home' });
  }
  clearSearchResult();
};

const loadMore = async () => {
  const SEARCH_ACTIONS = {
    contacts: 'conversationSearch/contactSearch',
    conversations: 'conversationSearch/conversationSearch',
    messages: 'conversationSearch/messageSearch',
    articles: 'conversationSearch/articleSearch',
  };

  if (
    uiFlags.value?.isFetching ||
    selectedTab.value === 'all' ||
    selectedField.value !== 'all'
  ) {
    return;
  }
  const tab = selectedTab.value;
  const nextPage = pages.value[tab] + 1;
  // Advance the counter only once that page is in the store. Advancing first
  // hid Load more for good after a failed request (records no longer equal
  // pages * PER_PAGE), and a request superseded by a new search must not move
  // the new search's counter.
  try {
    const landed = await store.dispatch(SEARCH_ACTIONS[tab], {
      q: query.value,
      page: nextPage,
      field: selectedField.value,
    });
    if (landed) pages.value[tab] = nextPage;
  } catch (error) {
    // Keep the page as it was so Load more retries the same page.
  }
};

onMounted(() => {
  store.dispatch('conversationSearch/clearSearchResults');
  if (query.value) runSearch();
});

onUnmounted(() => {
  query.value = '';
  store.dispatch('conversationSearch/clearSearchResults');
});
</script>

<template>
  <div class="search-page">
    <header class="search-header">
      <div class="search-header__bg">
        <div class="search-header__glow" />
        <div class="search-header__pattern" />
      </div>
      <div class="search-header__content">
        <div class="search-header__title-row">
          <button type="button" class="search-back" @click="onBack">
            <span class="i-lucide-chevron-left size-4" />
            {{ t('GENERAL_SETTINGS.BACK') }}
          </button>
          <div class="search-header__brand">
            <span class="search-header__icon i-lucide-search" />
            <div>
              <h1 class="search-header__heading">
                {{ t('SEARCH.INPUT_PLACEHOLDER') }}
              </h1>
              <p class="search-header__desc">
                {{ t('SEARCH.EMPTY_STATE_DEFAULT') }}
              </p>
            </div>
          </div>
        </div>
        <SearchHeader :initial-query="query" @search="onSearch" />
      </div>
    </header>

    <div v-if="query" class="search-tabs-bar">
      <SearchTabs
        :tabs="tabs"
        :selected-tab="activeTabIndex"
        @tab-change="onTabChange"
      />
      <div class="search-meta">
        <template v-if="isFetchingAny">
          <span class="i-lucide-loader-circle size-3.5 animate-spin" />
          {{ t('SEARCH.SEARCHING_DATA') }}
        </template>
        <template v-else>
          <strong>{{ totalSearchResultsCount }}</strong>
          · “{{ query }}”
        </template>
      </div>
    </div>

    <section class="search-body">
      <div v-if="showResultsSection" class="search-panel">
        <Policy
          :permissions="[...ROLES, CONTACT_PERMISSIONS]"
          class="search-block"
        >
          <SearchResultContactsList
            v-if="showContactsSection"
            :is-fetching="uiFlags.contact?.isFetching"
            :contacts="contacts"
            :query="query"
            :show-title="isSelectedTabAll"
          />
          <NextButton
            v-if="showViewMore.contacts"
            :label="t(`SEARCH.VIEW_MORE`)"
            icon="i-lucide-eye"
            slate
            sm
            outline
            @click="onTabChange('contacts')"
          />
        </Policy>

        <Policy
          :permissions="[...ROLES, ...CONVERSATION_PERMISSIONS]"
          class="search-block"
        >
          <SearchResultMessagesList
            v-if="showMessagesSection"
            :is-fetching="uiFlags.message?.isFetching"
            :messages="messages"
            :query="query"
            :show-title="isSelectedTabAll"
          />
          <NextButton
            v-if="showViewMore.messages"
            :label="t(`SEARCH.VIEW_MORE`)"
            icon="i-lucide-eye"
            slate
            sm
            outline
            @click="onTabChange('messages')"
          />
        </Policy>

        <Policy
          :permissions="[...ROLES, ...CONVERSATION_PERMISSIONS]"
          class="search-block"
        >
          <SearchResultConversationsList
            v-if="showConversationsSection"
            :is-fetching="uiFlags.conversation?.isFetching"
            :conversations="conversations"
            :query="query"
            :show-title="isSelectedTabAll"
          />
          <NextButton
            v-if="showViewMore.conversations"
            :label="t(`SEARCH.VIEW_MORE`)"
            icon="i-lucide-eye"
            slate
            sm
            outline
            @click="onTabChange('conversations')"
          />
        </Policy>

        <Policy
          v-if="isFeatureFlagEnabled(FEATURE_FLAGS.HELP_CENTER)"
          :permissions="[...ROLES, PORTAL_PERMISSIONS]"
          :feature-flag="FEATURE_FLAGS.HELP_CENTER"
          class="search-block"
        >
          <SearchResultArticlesList
            v-if="showArticlesSection"
            :is-fetching="uiFlags.article?.isFetching"
            :articles="articles"
            :query="query"
            :show-title="isSelectedTabAll"
          />
          <NextButton
            v-if="showViewMore.articles"
            :label="t(`SEARCH.VIEW_MORE`)"
            icon="i-lucide-eye"
            slate
            sm
            outline
            @click="onTabChange('articles')"
          />
        </Policy>

        <div v-if="showLoadMore" class="search-load-more">
          <NextButton
            v-if="!isSelectedTabAll"
            :label="t(`SEARCH.LOAD_MORE`)"
            icon="i-lucide-cloud-download"
            slate
            sm
            faded
            @click="loadMore"
          />
        </div>
      </div>

      <div v-else-if="showEmptySearchResults" class="search-empty">
        <span class="search-empty__icon i-lucide-search-x" />
        <p>{{ t('SEARCH.EMPTY_STATE_FULL', { query }) }}</p>
      </div>

      <div v-else-if="!query" class="search-empty search-empty--idle">
        <span class="search-empty__icon i-lucide-search" />
        <p>{{ t('SEARCH.EMPTY_STATE_DEFAULT') }}</p>
      </div>
    </section>
  </div>
</template>

<style scoped>
.search-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--wlab-bg-sidebar, var(--n-background));
  color: var(--wlab-text-primary, var(--n-slate-12));
}

.search-header {
  position: relative;
  flex-shrink: 0;
  padding: 16px 28px 18px;
  overflow: hidden;
}

.search-header__bg {
  position: absolute;
  inset: 0;
  background: var(--wlab-bg-card, var(--n-solid-2));
  border-bottom: 1px solid var(--wlab-border-light, var(--n-weak));
}

.search-header__glow {
  position: absolute;
  top: -45%;
  right: -8%;
  width: 360px;
  height: 360px;
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--wlab-accent, #6366f1) 14%, transparent) 0%,
    transparent 70%
  );
  pointer-events: none;
}

.search-header__pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    color-mix(in srgb, var(--wlab-text-primary, #fff) 4%, transparent) 1px,
    transparent 1px
  );
  background-size: 20px 20px;
  pointer-events: none;
}

.search-header__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  max-width: none;
  margin: 0;
}

.search-header__title-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.search-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  padding: 6px 10px;
  margin: 0;
  border: 1px solid var(--wlab-border-light, var(--n-weak));
  border-radius: 10px;
  background: transparent;
  color: var(--wlab-text-secondary, var(--n-slate-11));
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}

.search-back:hover {
  background: color-mix(in srgb, var(--wlab-accent, #6366f1) 10%, transparent);
  border-color: color-mix(
    in srgb,
    var(--wlab-accent, #6366f1) 35%,
    transparent
  );
  color: var(--wlab-text-primary, var(--n-slate-12));
}

.search-header__brand {
  display: flex;
  align-items: center;
  gap: 14px;
}

.search-header__icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid
    color-mix(in srgb, var(--wlab-accent, #6366f1) 35%, transparent);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--wlab-accent, #6366f1) 22%, transparent),
    color-mix(in srgb, var(--wlab-accent, #6366f1) 8%, transparent)
  );
  color: var(--wlab-accent, #a5b4fc);
  font-size: 20px;
}

.search-header__heading {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--wlab-text-primary, var(--n-slate-12));
}

.search-header__desc {
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--wlab-text-muted, var(--n-slate-11));
}

.search-tabs-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 12px 28px;
  border-bottom: 1px solid var(--wlab-border-light, var(--n-weak));
  background: var(--wlab-bg-card, var(--n-solid-2));
}

.search-meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--wlab-text-muted, var(--n-slate-11));
  white-space: nowrap;
}

.search-meta strong {
  color: var(--wlab-text-primary, var(--n-slate-12));
  font-weight: 600;
}

.search-body {
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  padding: 16px 28px 28px;
}

.search-panel,
.search-empty {
  width: 100%;
  max-width: none;
  margin: 0;
}

.search-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.search-load-more {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.search-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 280px;
  padding: 40px 24px;
  border: 1px dashed var(--wlab-border-light, var(--n-weak));
  border-radius: 16px;
  background: var(--wlab-bg-card, var(--n-solid-2));
  text-align: center;
}

.search-empty p {
  margin: 0;
  max-width: 420px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--wlab-text-muted, var(--n-slate-11));
}

.search-empty__icon {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  font-size: 22px;
  color: var(--wlab-accent, #6366f1);
  background: color-mix(in srgb, var(--wlab-accent, #6366f1) 12%, transparent);
  border: 1px solid
    color-mix(in srgb, var(--wlab-accent, #6366f1) 25%, transparent);
}

@media (max-width: 768px) {
  .search-header,
  .search-tabs-bar,
  .search-body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .search-header__heading {
    font-size: 18px;
  }

  .search-header__icon {
    width: 40px;
    height: 40px;
  }
}
</style>

<style>
/* Mix theme: force light surfaces on Search, matching Reports/Settings */
body.theme-mix .search-page {
  --wlab-bg-sidebar: #f8fafc;
  --wlab-bg-card: #ffffff;
  --wlab-border-light: #e5e7eb;
  --wlab-text-primary: #0f172a;
  --wlab-text-secondary: #1f2937;
  --wlab-text-muted: #64748b;
  --wlab-accent: #4f46e5;
  background: #f8fafc !important;
  color: #0f172a !important;
}

body.theme-mix .search-page .search-header__bg,
body.theme-mix .search-page .search-tabs-bar,
body.theme-mix .search-page .search-empty,
body.theme-mix .search-page .result-section,
body.theme-mix .search-page .search-input {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
  color: #0f172a !important;
}

body.theme-mix .search-page .search-header__heading,
body.theme-mix .search-page .search-meta strong,
body.theme-mix .search-page .result-item__info h5,
body.theme-mix .search-page .contact-item__body h5 {
  color: #0f172a !important;
}

body.theme-mix .search-page .search-header__desc,
body.theme-mix .search-page .search-meta,
body.theme-mix .search-page .search-empty p,
body.theme-mix .search-page .result-section__title h3 {
  color: #64748b !important;
}

/* Light + Mix: slightly stronger dotted header pattern */
body:not(.dark) .search-page .search-header__pattern,
body.theme-mix .search-page .search-header__pattern {
  background-image: radial-gradient(
    rgba(15, 23, 42, 0.14) 1px,
    transparent 1px
  ) !important;
  background-size: 18px 18px !important;
}

body:not(.dark) .search-page .search-header__bg,
body.theme-mix .search-page .search-header__bg {
  background: #eef2f7 !important;
}
</style>
